import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  linkWithPopup,
  signInWithCustomToken,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithCredential,
  signInWithPopup,
  signOut,
  reauthenticateWithPopup,
} from "firebase/auth";
import { auth } from "./firebase";
import { APP_URL } from "./config";
import { normalizeEmail } from "./utils";

const provider = new GoogleAuthProvider();
provider.setCustomParameters({
  prompt: "consent",
  access_type: "offline",
});

export async function signInWithGoogle() {
  const result = await signInWithPopup(auth, provider);
  const credential = GoogleAuthProvider.credentialFromResult(result);
  if (credential?.accessToken) {
    sessionStorage.setItem("googleAccessToken", credential.accessToken);
  }
  return result.user;
}

export async function signInWithGoogleIdToken(idToken) {
  if (!idToken) {
    throw new Error("Missing Google ID token.");
  }
  const credential = GoogleAuthProvider.credential(idToken);
  const result = await signInWithCredential(auth, credential);
  return result.user;
}

export async function signInWithDiscordToken(token) {
  if (!token) {
    throw new Error("Missing Discord sign-in token.");
  }
  const result = await signInWithCustomToken(auth, token);
  return result.user;
}

export async function registerWithEmailPassword(email, password) {
  const normalizedEmail = normalizeEmail(email);
  const result = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
  if (result?.user) {
    try {
      await sendEmailVerification(result.user, { url: `${APP_URL}/dashboard` });
    } catch (err) {
      console.warn("Failed to send verification email:", err);
    }
  }
  return result.user;
}

export async function signInWithEmailPassword(email, password) {
  const normalizedEmail = normalizeEmail(email);
  const result = await signInWithEmailAndPassword(auth, normalizedEmail, password);
  return result.user;
}

export async function resendVerificationEmail() {
  if (!auth.currentUser) return;
  await sendEmailVerification(auth.currentUser, { url: `${APP_URL}/dashboard` });
}

export async function linkGoogleAccount() {
  if (!auth.currentUser) {
    throw new Error("You must be signed in to link accounts.");
  }
  const result = await linkWithPopup(auth.currentUser, provider);
  // Return the credential as well: after linkWithPopup the SDK user.isAnonymous
  // flips to false, but the session's firebase.sign_in_provider claim STAYS
  // "anonymous" until a fresh credential sign-in. A caller upgrading an
  // anonymous guest (guest-vote migration) must signInWithCredential(credential)
  // to re-establish the session as google.com before any participant-level
  // Firestore write the rules gate on !isAnonymousProvider(). Callers that only
  // link an additional provider to an already-real account (settings) can ignore
  // the credential.
  return { user: result.user, credential: GoogleAuthProvider.credentialFromResult(result) };
}

export async function resetPassword(email) {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) return;
  const methods = await fetchSignInMethodsForEmail(auth, normalizedEmail);
  if (methods.includes("password")) {
    await sendPasswordResetEmail(auth, normalizedEmail, { url: `${APP_URL}/auth` });
    return;
  }
  if (methods.length > 0) {
    const { getFunctions, httpsCallable } = await import("firebase/functions");
    const functions = getFunctions();
    const sendPasswordResetInfo = httpsCallable(functions, "sendPasswordResetInfo");
    await sendPasswordResetInfo({ email: normalizedEmail }).catch(() => {});
  }
}

export function signOutUser() {
  return signOut(auth);
}

export function getStoredAccessToken() {
  return sessionStorage.getItem("googleAccessToken");
}

export async function getGoogleAccessToken({ forceRefresh = false } = {}) {
  const cached = getStoredAccessToken();
  if (cached && !forceRefresh) return cached;
  let result;
  if (auth.currentUser) {
    result = await reauthenticateWithPopup(auth.currentUser, provider);
  } else {
    result = await signInWithPopup(auth, provider);
  }
  const credential = GoogleAuthProvider.credentialFromResult(result);
  if (credential?.accessToken) {
    sessionStorage.setItem("googleAccessToken", credential.accessToken);
    return credential.accessToken;
  }
  return null;
}
